import {VNode} from 'snabbdom';
import {Component, jsx} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {IronContextRegistry} from 'common/report/context-registry';
import {GdprStorageManager} from 'common/contexts/storage-manager/gdpr';
import {DragAndDropManager} from 'common/report/contexts/query-contexts/drag-and-drop';
import {ReportLayoutManager} from 'common/report/contexts/report-layout';
import {WindowUrlManager} from 'common/contexts/url-manager/window';
import {CurrentProjectResource, ProjectsResource} from 'common/report/model-resources/projects';
import {CurrentUserResource} from 'common/report/model-resources/users';
import {getProjectId, getWorkspaceId, isEmbedMode, isPublicMode} from 'common/report/util';
import {HistoryManager} from 'common/contexts/history-manager';
import {createBookmarkStore} from 'common/report/stores/bookmarks';
import {createDashboardStore, shareDashboardBookmarks} from 'common/report/stores/dashboards';
import {DashboardSelectorController} from 'common/report/controllers/dashboard-selector-controller';
import {publicDashboardsClient} from 'common/report/queries/app-api/public-dashboards-client';
import {dashboardsClient} from 'common/report/queries/app-api/dashboards-client';
import {QueryContextManager} from 'common/query-context';
import {ProjectTopCreatorsResource} from 'common/report/model-resources/project-top-creators';
import {LocalStorageKey} from 'common/report/constants';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {UserLocalStorageHelper} from 'common/report/user-local-storage';
import {DARK_MODE_KEY} from 'common/widgets/chrome/chrome-header/constants';
import {PersistedDarkModeState} from 'common/widgets/chrome/chrome-header/types';
import {GlobalSearchController} from 'common/report/model-resources/global-search-controller';
import {AlertLimitsResource} from 'common/report/model-resources/alert-limits';

interface JsxProps {
  attrs?: EmptyObj;
}

// This is a blank, unstyled, non-shadow-DOM component designed only to wrap existing DOM structures so it can act as a Panel Context root for the DOM descendants under it.
// This context root is designed for standard user browser environments, not automated/isolated environments like WCT.
export class BrowserContextRoot extends Component<EmptyObj, JsxProps['attrs'], unknown, unknown, IronContextRegistry> {
  get config() {
    const localStorageManager = new GdprStorageManager(window.localStorage);
    const urlManager = new WindowUrlManager();
    const projectId = parseInt(getProjectId());
    const parsedWorkspaceId = parseInt(getWorkspaceId());
    const workspaceId = Number.isInteger(parsedWorkspaceId) ? parsedWorkspaceId : null;
    const bookmarkStore = createBookmarkStore({
      projectId,
      workspaceId,
    });

    const isPublicUrl = isPublicMode(urlManager);
    const dashboardStore = createDashboardStore();
    const dashboardSelectorController = new DashboardSelectorController({
      workspaceId,
      projectId,
      bookmarkID: null, // only used for managing dashboard membership for a specific bookmark
      dashboardsClient: isPublicUrl ? publicDashboardsClient : dashboardsClient,
      dashboardStore: isPublicUrl ? null : dashboardStore,
    });

    shareDashboardBookmarks(dashboardStore, bookmarkStore);

    const sessionStorageManager = new GdprStorageManager(window.sessionStorage);
    return {
      template: (): VNode => <div />,
      contexts: [
        `sessionStorageManager` as const,
        `queryContextManager` as const,
        `localStorageManager` as const,
        `urlManager` as const,
        `historyManager` as const,
        `reportLayoutManager` as const,
        `currentProject` as const,
      ],
      defaultContexts: {
        bookmarkStore,
        dashboardSelectorController,
        sessionStorageManager,
        localStorageManager,
        urlManager,
        reportLayoutManager: new ReportLayoutManager(),
        dragAndDropManager: new DragAndDropManager(),
        historyManager: new HistoryManager({mixpanel: window.mixpanel}),
        queryContextManager: new QueryContextManager(),

        // common model resources
        currentProject: new CurrentProjectResource({urlManager}),
        currentUser: new CurrentUserResource(),
        projects: new ProjectsResource(),
        projectTopCreators: new ProjectTopCreatorsResource({urlManager}),
        globalSearchController: new GlobalSearchController({sessionStorageManager}),
        alertLimits: new AlertLimitsResource({urlManager}),
      },
    };
  }

  connectedCallback(): void {
    super.connectedCallback();

    if (!isPublicMode() && getProjectId()) {
      this.getContext(`currentProject`).get();
    }

    if (isEmbedMode()) {
      const userLocalStorageDarkModeHelper: UserLocalStorageHelper<PersistedDarkModeState> = new UserLocalStorageHelper(
        {
          context: this.getContext(`localStorageManager`),
          namespace: LocalStorageKey.DarkMode,
        },
      );
      const persistedState = userLocalStorageDarkModeHelper.getItem({
        key: DARK_MODE_KEY,
        userId: userCookieUtils.getUserIdentityFromCookie()?.id,
      });
      const isOSDefault = window.matchMedia(`(prefers-color-scheme: dark)`).matches;
      if (persistedState?.darkMode || isOSDefault) {
        document.documentElement.classList.add(`dark-mode`);
      }
    }
  }
}

export const BrowserContextRootEl = defineAndMakeJsxFactory<JsxProps>(`mp-browser-context-root`, BrowserContextRoot);
export const BrowserContextRootReact = panel2react<JsxProps>(`mp-browser-context-root`, BrowserContextRoot);
